import { inject, Injectable } from '@angular/core';
import { Action, Actions, ofActionDispatched, Selector, State, StateContext, Store } from '@ngxs/store';
import { ResetStateDefaults } from '../base-action-class.model';
import { ChecklistTemplateEntryConditionAction } from './checklist-template-entry-condition.action';
import { finalize, Observable, of, switchMap, throwError, zip } from 'rxjs';
import { ChecklistTemplateDetailXsState } from '../checklist-template/checklist-template-detail-xs.state';
import { ChecklistTemplateEntryRequestService } from '../../services/request/checklist-template-entry-request.service';
import {
  ChecklistTemplateEntryCondition
} from '../../models/checklist-template-entry/checklist-template-entry-condition.model';
import {
  ChecklistTemplateEntryConditionDropdownEntryType,
  ChecklistTemplateEntryConditionMode
} from '../../enums/checklist-template/checklist-template-entry-condition.enum';
import { delay, map, takeUntil, tap } from 'rxjs/operators';
import { ChecklistTemplateDetailAction } from '../checklist-template/checklist-template-detail.action';
import { ChecklistTemplateEntry } from '../../models/checklist-template-entry/checklist-template-entry.model';
import {
  ChecklistTemplateEntryRange
} from '../../models/checklist-template-entry/checklist-template-entry-range.model';
import {
  ChecklistTemplateEntryPossibleConditions
} from '../../models/checklist-template-entry/checklist-template-entry-possible-conditions.model';
import {
  ChecklistTemplateVersionEntryRangeType
} from '../../enums/checklist-template/checklist-template-version-entry-range-type.enum';
import {
  ChecklistTemplateEntryConditionDropdownEntry
} from '../../models/checklist-template-entry/checklist-template-entry-condition-dropdown-entry.model';
import { ChecklistEntryRenderOption } from '../../enums/checklist-template/checklist-entry-render-option';

interface ChecklistTemplateEntryConditionStateModel {
  active: boolean;
  checklistTemplateEntryId: string;
  checklistTemplateEntryConditionId: string;
  checklistTemplateEntryCondition: ChecklistTemplateEntryCondition;
  updatedChecklistTemplateEntryCondition: ChecklistTemplateEntryCondition;
  updatedChecklistTemplateEntryRange: ChecklistTemplateEntryRange;
  possibleConditions: ChecklistTemplateEntryPossibleConditions[];
  entryDropdownEntries: ChecklistTemplateEntryConditionDropdownEntry[];
  conditionDropdownEntriesOfEntry: Map<string, ChecklistTemplateEntryConditionDropdownEntry[]>;
  sourceChecklistTemplateEntry: ChecklistTemplateEntry;
  mode: ChecklistTemplateEntryConditionMode;
  initialized: boolean;
  inProgress: boolean;
}

const getDefaults = (): ChecklistTemplateEntryConditionStateModel => ({
  active: false,
  checklistTemplateEntryId: null,
  checklistTemplateEntryConditionId: null,
  checklistTemplateEntryCondition: null,
  updatedChecklistTemplateEntryCondition: null,
  updatedChecklistTemplateEntryRange: null,
  possibleConditions: null,
  entryDropdownEntries: null,
  conditionDropdownEntriesOfEntry: null,
  sourceChecklistTemplateEntry: null,
  mode: null,
  initialized: false,
  inProgress: false
});

@State<ChecklistTemplateEntryConditionStateModel>({
  name: 'checklistTemplateEntryCondition',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistTemplateEntryConditionXsState {

  //region INJECTIONS
  private store = inject(Store);
  private checklistTemplateEntryRequestService = inject(ChecklistTemplateEntryRequestService);
  private actions = inject(Actions);
  //endregion

  //region SELECTORS
  @Selector()
  static active(state: ChecklistTemplateEntryConditionStateModel): boolean {
    return state.active;
  }

  @Selector()
  static mode(state: ChecklistTemplateEntryConditionStateModel): ChecklistTemplateEntryConditionMode {
    return state.mode;
  }

  @Selector()
  static checklistTemplateEntryId(state: ChecklistTemplateEntryConditionStateModel): string {
    return state.checklistTemplateEntryId;
  }

  @Selector()
  static checklistTemplateEntryCondition(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryCondition {
    return state.checklistTemplateEntryCondition ? { ...state.checklistTemplateEntryCondition } : null;
  }

  @Selector()
  static updatedChecklistTemplateEntryCondition(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryCondition {
    return state.updatedChecklistTemplateEntryCondition ? { ...state.updatedChecklistTemplateEntryCondition } : null;
  }

  @Selector()
  static updatedChecklistTemplateEntryRange(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryRange {
    return state.updatedChecklistTemplateEntryRange ? { ...state.updatedChecklistTemplateEntryRange } : null;
  }

  @Selector()
  static entryDropdownEntries(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryConditionDropdownEntry[] {
    return state.entryDropdownEntries || [];
  }

  @Selector()
  static conditionDropdownEntries(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryConditionDropdownEntry[] {
    if (!state.conditionDropdownEntriesOfEntry || !state.updatedChecklistTemplateEntryCondition?.sourceEntryId) {
      return [];
    }

    return state.conditionDropdownEntriesOfEntry.get(state.updatedChecklistTemplateEntryCondition?.sourceEntryId);
  }

  @Selector()
  static typeOfConditionDropdownEntries(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryConditionDropdownEntryType {
    if (!state.conditionDropdownEntriesOfEntry || !state.updatedChecklistTemplateEntryCondition?.sourceEntryId) {
      return null;
    }

    const conditionDropdownEntries: ChecklistTemplateEntryConditionDropdownEntry[]
      = (state.conditionDropdownEntriesOfEntry.get(state.updatedChecklistTemplateEntryCondition?.sourceEntryId) || []);

    if (conditionDropdownEntries.some((entry: ChecklistTemplateEntryConditionDropdownEntry) =>
      entry.type === ChecklistTemplateEntryConditionDropdownEntryType.ASSESSMENT)
    ) {
      return ChecklistTemplateEntryConditionDropdownEntryType.ASSESSMENT;
    }

    if (conditionDropdownEntries.some((entry: ChecklistTemplateEntryConditionDropdownEntry) =>
      entry.type === ChecklistTemplateEntryConditionDropdownEntryType.OPTION)
    ) {
      return ChecklistTemplateEntryConditionDropdownEntryType.OPTION;
    }

    if (conditionDropdownEntries.some((entry: ChecklistTemplateEntryConditionDropdownEntry) =>
      entry.type === ChecklistTemplateEntryConditionDropdownEntryType.RANGE)
    ) {
      return ChecklistTemplateEntryConditionDropdownEntryType.RANGE;
    }
  }

  @Selector()
  static sourceChecklistTemplateEntry(state: ChecklistTemplateEntryConditionStateModel): ChecklistTemplateEntry {
    return state.sourceChecklistTemplateEntry;
  }

  @Selector()
  static selectedPossibleConditionsEntry(
    state: ChecklistTemplateEntryConditionStateModel
  ): ChecklistTemplateEntryPossibleConditions {
    if (!state.possibleConditions || !state.updatedChecklistTemplateEntryCondition?.sourceEntryId) {
      return null;
    }

    return state.possibleConditions.find((possibleCondition: ChecklistTemplateEntryPossibleConditions) =>
      possibleCondition.checklistTemplateEntryId == state.updatedChecklistTemplateEntryCondition?.sourceEntryId
    );
  }

  @Selector()
  static isMultipleOptionsEnabled(state: ChecklistTemplateEntryConditionStateModel): boolean {
    if (!state.conditionDropdownEntriesOfEntry || !state.updatedChecklistTemplateEntryCondition?.sourceEntryId) {
      return null;
    }

    return state.possibleConditions?.find((condition: ChecklistTemplateEntryPossibleConditions) =>
      condition.checklistTemplateEntryId === state.updatedChecklistTemplateEntryCondition?.sourceEntryId
    )?.multipleOptionsEnabled;
  }

  @Selector()
  static initialized(state: ChecklistTemplateEntryConditionStateModel): boolean {
    return state.initialized;
  }

  @Selector()
  static inProgress(state: ChecklistTemplateEntryConditionStateModel): boolean {
    return state.inProgress;
  }

  //endregion

  //region ACTIONS
  @Action(ChecklistTemplateEntryConditionAction.Create)
  public create(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>,
    action: ChecklistTemplateEntryConditionAction.Create
  ): Observable<void> {
    const state: ChecklistTemplateEntryConditionStateModel = ctx.getState();

    if (state.active) {
      return throwError(() => new Error('State is already active!'));
    }

    if (!action.checklistTemplateEntryId) {
      return throwError(() => new Error('Checklist template entry id cannot be null!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      return throwError(() => new Error('Checklist template detail page is not initialized!'));
    }

    ctx.patchState({
      active: true,
      checklistTemplateEntryId: action.checklistTemplateEntryId,
      mode: ChecklistTemplateEntryConditionMode.CREATE,
      initialized: false,
      inProgress: false
    });

    return this.checklistTemplateEntryRequestService.getPossibleConditionsForEntry(
      checklistTemplateId,
      action.checklistTemplateEntryId
    )
      .pipe(
        takeUntil(this.actions.pipe(
          ofActionDispatched(ChecklistTemplateEntryConditionAction.ResetState)
        )),
        map((possibleConditions: ChecklistTemplateEntryPossibleConditions[]) => {
          this.setSelectableEntities(ctx, possibleConditions, null);
          ctx.patchState({ initialized: true });
        })
      );
  }

  @Action(ChecklistTemplateEntryConditionAction.Edit)
  public edit(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>,
    action: ChecklistTemplateEntryConditionAction.Edit
  ): Observable<void> {
    const state: ChecklistTemplateEntryConditionStateModel = ctx.getState();

    if (state.active) {
      return throwError(() => new Error('State is already active!'));
    }

    if (!action.checklistTemplateEntryId) {
      return throwError(() => new Error('Checklist template entry id cannot be null!'));
    }

    if (!action.checklistTemplateEntryConditionId) {
      return throwError(() => new Error('Checklist template entry condition id cannot be null!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      return throwError(() => new Error('Checklist template detail page is not initialized!'));
    }

    ctx.patchState({
      active: true,
      checklistTemplateEntryId: action.checklistTemplateEntryId,
      checklistTemplateEntryConditionId: action.checklistTemplateEntryConditionId,
      mode: ChecklistTemplateEntryConditionMode.EDIT,
      initialized: false,
      inProgress: false
    });

    return zip(
      this.checklistTemplateEntryRequestService.getChecklistTemplateEntryCondition(
        checklistTemplateId,
        action.checklistTemplateEntryId,
        action.checklistTemplateEntryConditionId
      ),
      this.checklistTemplateEntryRequestService.getPossibleConditionsForEntryAndCondition(
        checklistTemplateId,
        action.checklistTemplateEntryId,
        action.checklistTemplateEntryConditionId
      )
    )
      .pipe(
        switchMap(([checklistTemplateEntryCondition, possibleConditions]
                   : [ChecklistTemplateEntryCondition, ChecklistTemplateEntryPossibleConditions[]]) => {
          return this.checklistTemplateEntryRequestService.getChecklistTemplateEntry(
            checklistTemplateId,
            checklistTemplateEntryCondition.sourceEntryId
          ).pipe(
            map((sourceChecklistTemplateEntry: ChecklistTemplateEntry) => ([
              checklistTemplateEntryCondition,
              possibleConditions,
              sourceChecklistTemplateEntry
            ])),
            delay(300)
          );
        }),
        takeUntil(this.actions.pipe(
          ofActionDispatched(ChecklistTemplateEntryConditionAction.ResetState)
        )),
        map(([checklistTemplateEntryCondition, possibleConditions, sourceChecklistTemplateEntry]
             : [ChecklistTemplateEntryCondition, ChecklistTemplateEntryPossibleConditions[], ChecklistTemplateEntry]) => {
          this.setSelectableEntities(ctx, possibleConditions, sourceChecklistTemplateEntry.renderOption);
          ctx.patchState({
            checklistTemplateEntryCondition: checklistTemplateEntryCondition,
            updatedChecklistTemplateEntryCondition: { ...checklistTemplateEntryCondition },
            updatedChecklistTemplateEntryRange: { ...checklistTemplateEntryCondition.range },
            sourceChecklistTemplateEntry,
            initialized: true
          });
        })
      );
  }

  @Action(ChecklistTemplateEntryConditionAction.Update)
  public update(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>,
    action: ChecklistTemplateEntryConditionAction.Update
  ): void {
    const state: ChecklistTemplateEntryConditionStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      throw new Error('Checklist template detail page is not initialized!');
    }

    const previousSourceEntryId = state.updatedChecklistTemplateEntryCondition?.sourceEntryId
      || state.checklistTemplateEntryCondition?.sourceEntryId;

    const updatedChecklistTemplateEntryCondition: ChecklistTemplateEntryCondition = {
      ...(state.updatedChecklistTemplateEntryCondition || {}),
      ...(action.checklistTemplateEntryCondition || {})
    } as ChecklistTemplateEntryCondition;

    if (!!updatedChecklistTemplateEntryCondition?.checklistTemplateEntryOptionIds?.length) {
      updatedChecklistTemplateEntryCondition.checklistTemplateEntryRangeId = null;
    }

    if (!!updatedChecklistTemplateEntryCondition.checklistTemplateEntryRangeId) {
      updatedChecklistTemplateEntryCondition.checklistTemplateEntryOptionIds = null;
    }

    ctx.patchState({
      updatedChecklistTemplateEntryCondition,
      updatedChecklistTemplateEntryRange: null
    });

    if (previousSourceEntryId !== updatedChecklistTemplateEntryCondition?.sourceEntryId) {
      ctx.patchState({
        sourceChecklistTemplateEntry: null
      });

      if (updatedChecklistTemplateEntryCondition?.sourceEntryId !== null) {
        this.checklistTemplateEntryRequestService.getChecklistTemplateEntry(
          checklistTemplateId,
          updatedChecklistTemplateEntryCondition.sourceEntryId
        ).subscribe((sourceChecklistTemplateEntry: ChecklistTemplateEntry) => {
          ctx.patchState({
            sourceChecklistTemplateEntry
          });
        });
      }
    }

    // this.checklistTemplateEntryRequestService.getChecklistTemplateEntry(checklistTemplateId, state.checklistTemplateEntryId)
    //   .subscribe((checklistTemplateEntry: ChecklistTemplateEntry) => {
    //
    //     let existingChecklistTemplateEntryCondition: ChecklistTemplateEntryCondition;
    //
    //     //Only load existing values if the source entry was changed
    //     if (updatedChecklistTemplateEntryCondition.sourceEntryId !== previousSourceEntryId) {
    //
    //       //check if condition already exists
    //       existingChecklistTemplateEntryCondition = checklistTemplateEntry.conditions
    //         .find(c => c.sourceEntryId == updatedChecklistTemplateEntryCondition.sourceEntryId);
    //
    //       if (existingChecklistTemplateEntryCondition) {
    //         updatedChecklistTemplateEntryCondition = existingChecklistTemplateEntryCondition;
    //         ctx.patchState({
    //           checklistTemplateEntryCondition: updatedChecklistTemplateEntryCondition,
    //           checklistTemplateEntryConditionId: updatedChecklistTemplateEntryCondition.id,
    //           updatedChecklistTemplateEntryCondition: {...updatedChecklistTemplateEntryCondition},
    //           updatedChecklistTemplateEntryRange: {...updatedChecklistTemplateEntryCondition.range},
    //           mode: ChecklistTemplateEntryConditionMode.EDIT,
    //           initialized: false,
    //         });
    //       }
    //     }
    //
    //     ctx.patchState({
    //       updatedChecklistTemplateEntryCondition,
    //       updatedChecklistTemplateEntryRange: null
    //     });
    //
    //     if (previousSourceEntryId !== updatedChecklistTemplateEntryCondition?.sourceEntryId) {
    //       ctx.patchState({
    //         sourceChecklistTemplateEntry: null
    //       });
    //
    //       if (updatedChecklistTemplateEntryCondition?.sourceEntryId !== null) {
    //         this.checklistTemplateEntryRequestService.getChecklistTemplateEntry(
    //           checklistTemplateId,
    //           updatedChecklistTemplateEntryCondition.sourceEntryId
    //         ).subscribe((sourceChecklistTemplateEntry: ChecklistTemplateEntry) => {
    //           ctx.patchState({
    //             sourceChecklistTemplateEntry
    //           });
    //           if (existingChecklistTemplateEntryCondition) {
    //             ctx.patchState({initialized: true});
    //           }
    //         });
    //       }
    //     }
    //   });
  }

  @Action(ChecklistTemplateEntryConditionAction.UpdateRange)
  public updateRange(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>,
    action: ChecklistTemplateEntryConditionAction.UpdateRange
  ): void {
    const state: ChecklistTemplateEntryConditionStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({
      updatedChecklistTemplateEntryCondition: {
        ...(state.updatedChecklistTemplateEntryCondition || {}),
        checklistTemplateEntryOptionIds: null,
        checklistTemplateEntryRangeId: null
      } as ChecklistTemplateEntryCondition,
      updatedChecklistTemplateEntryRange: {
        ...(state.updatedChecklistTemplateEntryRange || {}),
        ...(action.checklistTemplateEntryRange || {}),
        id: state.checklistTemplateEntryCondition?.range?.rangeType
        === ChecklistTemplateVersionEntryRangeType.PREDEFINED
          ? null
          : state.checklistTemplateEntryCondition?.range?.id,
        rangeType: ChecklistTemplateVersionEntryRangeType.CONDITION
      } as ChecklistTemplateEntryRange
    });
  }

  @Action(ChecklistTemplateEntryConditionAction.Save)
  public save(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateEntryConditionStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!state.updatedChecklistTemplateEntryCondition) {
      return throwError(() => new Error('Cannot update condition! Condition is null!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      return throwError(() => new Error('Checklist template detail page is not initialized!'));
    }

    let request: Observable<any> = of();

    if (state.mode === ChecklistTemplateEntryConditionMode.CREATE) {
      request = this.checklistTemplateEntryRequestService.createChecklistTemplateEntryCondition(
        checklistTemplateId,
        state.checklistTemplateEntryId,
        {
          ...(state.updatedChecklistTemplateEntryCondition || {}),
          range: state.updatedChecklistTemplateEntryRange
        } as ChecklistTemplateEntryCondition
      );
    } else if (state.mode === ChecklistTemplateEntryConditionMode.EDIT) {
      request = this.checklistTemplateEntryRequestService.updateChecklistTemplateEntryCondition(
        checklistTemplateId,
        state.checklistTemplateEntryId,
        state.checklistTemplateEntryConditionId,
        {
          ...(state.updatedChecklistTemplateEntryCondition || {}),
          range: state.updatedChecklistTemplateEntryRange
        } as ChecklistTemplateEntryCondition
      );
    }

    ctx.patchState({
      inProgress: true
    });

    return request
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryConditionChanged());
        }),
        tap(() => {
          this.store.dispatch(new ChecklistTemplateEntryConditionAction.ResetState());
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false
          });
        })
      );
  }

  @Action(ChecklistTemplateEntryConditionAction.Delete)
  public delete(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateEntryConditionStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    const checklistTemplateId = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      return throwError(() => new Error('Checklist template detail page is not initialized!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return this.checklistTemplateEntryRequestService.deleteChecklistTemplateEntryCondition(
      checklistTemplateId,
      state.checklistTemplateEntryId,
      state.checklistTemplateEntryConditionId
    )
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryConditionChanged());
        }),
        tap(() => {
          this.store.dispatch(new ChecklistTemplateEntryConditionAction.ResetState());
        }),
        finalize(() => {
          ctx.patchState({
            inProgress: false
          });
        })
      );
  }

  @Action([
    ChecklistTemplateEntryConditionAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<ChecklistTemplateEntryConditionStateModel>): void {
    ctx.setState(getDefaults());
  }

  //endregion

  //region HELPERS
  private setSelectableEntities(
    ctx: StateContext<ChecklistTemplateEntryConditionStateModel>,
    checklistTemplateEntryPossibleConditions: ChecklistTemplateEntryPossibleConditions[],
    renderOption: ChecklistEntryRenderOption | null
  ): void {
    const selectableChecklistTemplateEntries: ChecklistTemplateEntryConditionDropdownEntry[] = [];
    const selectableConditionsOfChecklistTemplateEntries: Map<string, ChecklistTemplateEntryConditionDropdownEntry[]>
      = new Map();

    for (const checklistTemplateEntryPossibleCondition
      of checklistTemplateEntryPossibleConditions) {

      if (!selectableConditionsOfChecklistTemplateEntries
        .has(checklistTemplateEntryPossibleCondition.checklistTemplateEntryId)) {
        selectableConditionsOfChecklistTemplateEntries
          .set(checklistTemplateEntryPossibleCondition.checklistTemplateEntryId, []);
        selectableChecklistTemplateEntries.push(ChecklistTemplateEntryConditionDropdownEntry.forEntry(
          checklistTemplateEntryPossibleCondition.checklistTemplateEntryId,
          checklistTemplateEntryPossibleCondition.conditionReference,
          checklistTemplateEntryPossibleCondition.renderType
        ));
      }

      for (const checklistTemplateEntryCondition
        of checklistTemplateEntryPossibleCondition.possibleConditions) {
        const selectableConditions: ChecklistTemplateEntryConditionDropdownEntry[]
          = selectableConditionsOfChecklistTemplateEntries.get(
          checklistTemplateEntryPossibleCondition.checklistTemplateEntryId
        );
        selectableConditions.push(ChecklistTemplateEntryConditionDropdownEntry
          .forPossibleCondition(checklistTemplateEntryCondition, checklistTemplateEntryPossibleCondition));
        selectableConditionsOfChecklistTemplateEntries
          .set(checklistTemplateEntryPossibleCondition.checklistTemplateEntryId, selectableConditions);
      }

    }

    ctx.patchState({
      entryDropdownEntries: selectableChecklistTemplateEntries,
      conditionDropdownEntriesOfEntry: selectableConditionsOfChecklistTemplateEntries,
      possibleConditions: checklistTemplateEntryPossibleConditions
    });
  }

  //endregion
}
